import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  generateSmartMappings as generateSmartMappingsBase,
  transformDataWithMapping as transformDataWithMappingBase,
  validateMappings as validateMappingsBase,
  type ColumnMapping as ColumnMappingBase
} from '@/lib/column-mapping-utils';
import { syncAccountFromHubSpot } from '@/lib/hubspot-wrapper';
import { sendSlackNotification } from '@/lib/slack-wrapper';

// Re-export from column-mapping-utils for compatibility
export type ColumnMapping = ColumnMappingBase;
export const generateSmartMappings = generateSmartMappingsBase;
export const transformDataWithMapping = transformDataWithMappingBase;
export const validateMappings = validateMappingsBase;

export interface ContactDatabaseField {
  name: string;
  label: string;
  required: boolean;
  type: 'text' | 'number' | 'email' | 'url' | 'date' | 'boolean';
  description?: string;
  validation?: (value: any) => boolean;
  transform?: (value: any) => any;
  isCustom?: boolean;
}

// Fetch custom fields for contacts from the database
export async function fetchCustomContactFields(): Promise<ContactDatabaseField[]> {
  const { data, error } = await supabase
    .from('custom_company_fields')
    .select('column_name, display_label, data_type, description')
    .eq('is_active', true)
    .eq('target_table', 'contacts')
    .order('created_at', { ascending: true });

  if (error || !data) {
    console.warn('Failed to fetch custom contact fields:', error?.message);
    return [];
  }

  return data.map((row: any) => ({
    name: row.column_name,
    label: row.display_label,
    required: false,
    type: row.data_type as ContactDatabaseField['type'],
    description: row.description || `Custom field: ${row.display_label}`,
    isCustom: true,
    ...(row.data_type === 'number' ? {
      transform: (value: any) => {
        if (!value) return null;
        const numValue = parseFloat(String(value).replace(/[^\d.-]/g, ''));
        return isNaN(numValue) ? null : numValue;
      }
    } : {}),
    ...(row.data_type === 'boolean' ? {
      transform: (value: any) => {
        if (typeof value === 'boolean') return value;
        const str = String(value).toLowerCase();
        return ['true', 'yes', '1'].includes(str);
      }
    } : {}),
  }));
}

// Merge static contact fields with custom fields
export function mergeContactFieldLists(customFields: ContactDatabaseField[]): ContactDatabaseField[] {
  return [...CONTACT_DB_FIELDS, ...customFields];
}

// ============================================
// Contact database fields — aligned to DB `contacts` table
// ============================================
export const CONTACT_DB_FIELDS: ContactDatabaseField[] = [
  {
    name: 'first_name',
    label: 'First Name',
    required: true,
    type: 'text',
    description: 'Contact\'s first name'
  },
  {
    name: 'last_name',
    label: 'Last Name',
    required: true,
    type: 'text',
    description: 'Contact\'s last name'
  },
  {
    name: 'primary_email',
    label: 'Email Address',
    required: true,
    type: 'email',
    description: 'Primary email address',
    validation: (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
  },
  {
    name: 'title',
    label: 'Job Title',
    required: false,
    type: 'text',
    description: 'Current position or role'
  },
  {
    name: 'department',
    label: 'Department',
    required: false,
    type: 'text',
    description: 'Department or team'
  },
  {
    name: 'seniority',
    label: 'Seniority Level',
    required: false,
    type: 'text',
    description: 'Seniority level (e.g., Senior, Director, VP, C-Suite)'
  },
  {
    name: 'primary_phone',
    label: 'Phone Number',
    required: false,
    type: 'text',
    description: 'Primary contact phone number'
  },
  {
    name: 'primary_linkedin',
    label: 'LinkedIn Profile',
    required: false,
    type: 'url',
    description: 'Personal LinkedIn profile URL'
  },
  {
    name: 'full_name',
    label: 'Full Name',
    required: false,
    type: 'text',
    description: 'Full display name (auto-generated from first + last if blank)'
  },
  {
    name: 'source',
    label: 'Source',
    required: false,
    type: 'text',
    description: 'Where this contact was sourced from (e.g., csv_import, linkedin, hubspot)'
  },
  // Company matching fields (used during CSV import, not stored in contacts table)
  {
    name: 'company_name',
    label: 'Company Name',
    required: false,
    type: 'text',
    description: 'Company name for account matching'
  },
  {
    name: 'company_domain',
    label: 'Company Domain',
    required: false,
    type: 'text',
    description: 'Company domain for account matching',
    transform: (value: string) => value?.replace(/^https?:\/\//, '').replace(/\/$/, '').toLowerCase()
  },
  {
    name: 'company_linkedin',
    label: 'Company LinkedIn',
    required: false,
    type: 'url',
    description: 'Company LinkedIn page URL for account matching'
  },
];

// ============================================
// Account matching and creation logic
// ============================================
export interface AccountMatchResult {
  account: any | null;
  isNewAccount: boolean;
  matchMethod?: 'linkedin' | 'domain' | 'created';
}

export async function findOrCreateAccount(
  contactData: any,
  clientId: number
): Promise<AccountMatchResult> {
  try {
    let account = null;
    let matchMethod: 'linkedin' | 'domain' | 'created' | undefined;
    let hubspotData = null;

    // Extract company information from contact
    const companyLinkedin = contactData.company_linkedin;
    const companyDomain = contactData.company_domain;
    const companyName = contactData.company_name;

    // First: Try to match by LinkedIn company URL via company_identifiers
    if (companyLinkedin) {
      const { data: linkedinMatch } = await supabase
        .from('company_identifiers')
        .select('company_id, companies(*)')
        .eq('identifier_type', 'linkedin')
        .ilike('identifier_value', `%${extractLinkedinCompanyName(companyLinkedin)}%`)
        .limit(1)
        .maybeSingle();

      if (linkedinMatch?.companies) {
        account = { ...(linkedinMatch.companies as any), id: linkedinMatch.company_id };
        matchMethod = 'linkedin';
      }
    }

    // Second: Try to match by domain via company_identifiers
    if (!account && companyDomain) {
      const { data: domainMatch } = await supabase
        .from('company_identifiers')
        .select('company_id, companies(*)')
        .eq('identifier_type', 'domain')
        .eq('identifier_value', companyDomain.toLowerCase())
        .limit(1)
        .maybeSingle();

      if (domainMatch?.companies) {
        account = { ...(domainMatch.companies as any), id: domainMatch.company_id };
        matchMethod = 'domain';
      }
    }

    // Third: Try to match by company name (fuzzy) in companies table
    if (!account && companyName) {
      const { data: nameMatches } = await supabase
        .from('companies')
        .select('*')
        .ilike('name', `%${companyName}%`)
        .limit(5);

      if (nameMatches && nameMatches.length > 0) {
        const mapped = nameMatches.map((c: any) => ({ ...c, company_name: c.name }));
        account = findBestCompanyNameMatch(companyName, mapped);
        if (account) {
          matchMethod = 'domain';
        }
      }
    }

    // If no account found, create via ingest-accounts Edge Function
    if (!account && (companyName || companyDomain)) {
      // Try to enrich with HubSpot data
      if (companyDomain || companyName) {
        try {
          const hubspotResult = await syncAccountFromHubSpot(companyDomain, companyName, clientId);
          if (hubspotResult) {
            hubspotData = hubspotResult.hubspotData;
            console.log('Enriched account data with HubSpot:', hubspotResult.enrichedFields);
          }
        } catch (error) {
          console.warn('HubSpot enrichment failed:', error);
        }
      }

      const accountPayload = {
        name: companyName || extractCompanyFromDomain(companyDomain),
        company_name: companyName || extractCompanyFromDomain(companyDomain),
        domain: companyDomain,
        linkedin_url: companyLinkedin,
        account_status: 'Active',
        ...(hubspotData ? {
          industry: hubspotData.properties.industry,
          annual_revenue: hubspotData.properties.annualrevenue ? parseInt(hubspotData.properties.annualrevenue) : null,
          employee_count: hubspotData.properties.numberofemployees,
          description: hubspotData.properties.description,
          city: hubspotData.properties.city,
          state: hubspotData.properties.state,
          country: hubspotData.properties.country,
        } : {})
      };

      const { data: ingestResult, error: ingestError } = await supabase.functions.invoke('ingest-accounts', {
        body: {
          client_id: String(clientId),
          accounts: [accountPayload],
        },
      });

      if (ingestError) {
        console.error('Error creating company via Edge Function:', ingestError);
        throw ingestError;
      }

      const resultItem = ingestResult?.results?.[0];
      if (resultItem?.company_id) {
        const { data: newCompany } = await supabase
          .from('companies')
          .select('*')
          .eq('id', resultItem.company_id)
          .single();

        account = newCompany ? { ...newCompany, company_name: newCompany.name } : null;
      }

      matchMethod = 'created';

      sendSlackNotification('account_created', {
        account: account,
        includeHubSpotData: !!hubspotData,
        hubspotData: hubspotData
      });

      return {
        account,
        isNewAccount: true,
        matchMethod
      };
    }

    return {
      account,
      isNewAccount: false,
      matchMethod
    };

  } catch (error) {
    console.error('Error in findOrCreateAccount:', error);
    throw error;
  }
}

// Helper function to extract company name from LinkedIn URL
function extractLinkedinCompanyName(linkedinUrl: string): string {
  try {
    const url = new URL(linkedinUrl);
    const pathParts = url.pathname.split('/');
    const companyIndex = pathParts.indexOf('company');
    if (companyIndex !== -1 && pathParts[companyIndex + 1]) {
      return pathParts[companyIndex + 1];
    }
    return '';
  } catch {
    const match = linkedinUrl.match(/company\/([^\/\?]+)/);
    return match ? match[1] : '';
  }
}

// Helper function to extract company name from domain
function extractCompanyFromDomain(domain: string): string {
  if (!domain) return 'Unknown Company';
  const mainPart = domain.split('.')[0];
  return mainPart.charAt(0).toUpperCase() + mainPart.slice(1).replace(/[-_]/g, ' ');
}

// Helper function to find best company name match using similarity
function findBestCompanyNameMatch(searchName: string, candidates: any[]): any | null {
  let bestMatch = null;
  let bestScore = 0;

  const normalizedSearch = searchName.toLowerCase().trim();

  for (const candidate of candidates) {
    const candidateName = candidate.company_name.toLowerCase().trim();
    const score = calculateSimilarity(normalizedSearch, candidateName);

    if (score > bestScore && score > 0.7) {
      bestScore = score;
      bestMatch = candidate;
    }
  }

  return bestMatch;
}

function calculateSimilarity(str1: string, str2: string): number {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;

  if (longer.length === 0) return 1.0;

  const distance = levenshteinDistance(longer, shorter);
  return (longer.length - distance) / longer.length;
}

function levenshteinDistance(str1: string, str2: string): number {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));

  for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;

  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const substitutionCost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + substitutionCost
      );
    }
  }

  return matrix[str2.length][str1.length];
}

// ============================================
// Process contacts with company matching
// Sets company_id (not account_id) on each contact
// ============================================
export async function processContactsWithAccountMatching(
  contactsData: any[],
  clientId: number
): Promise<{
  processedContacts: any[];
  accountMatches: { [key: string]: AccountMatchResult };
  errors: string[];
}> {
  const processedContacts: any[] = [];
  const accountMatches: { [key: string]: AccountMatchResult } = {};
  const errors: string[] = [];

  for (let i = 0; i < contactsData.length; i++) {
    const contact = contactsData[i];

    try {
      // Find or create company for this contact
      const matchResult = await findOrCreateAccount(contact, clientId);

      if (matchResult.account) {
        const processedContact = {
          ...contact,
          company_id: matchResult.account.id,
          // Remove company matching fields — they're not stored in contacts table
          company_name: undefined,
          company_domain: undefined,
          company_linkedin: undefined,
        };

        processedContacts.push(processedContact);
        accountMatches[`contact_${i}`] = matchResult;
      } else {
        errors.push(`Row ${i + 1}: Could not create or find company for ${contact.first_name} ${contact.last_name}`);
      }
    } catch (error) {
      console.error(`Error processing contact ${i + 1}:`, error);
      errors.push(`Row ${i + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  return {
    processedContacts,
    accountMatches,
    errors
  };
}
